/**
 * HolidaysCard — the dashboard "Upcoming" card.
 *
 * Quick task 260512-uat-batch (F-05a, 2026-05-12) — Petra requested a
 * holidays widget on the admin dashboard. Petra confirmed scope:
 * **admin-only** — manager / PM / plain user / freelance don't see it.
 *
 * MKS-392 (2026-10-20) widens it, behind `mks_392_upcoming_celebrations`:
 * the same card also lists team name days (jmeniny) and team birthdays for
 * the look-ahead window, today's items are highlighted, and every internal
 * user sees it (the audience change itself lives in DashboardPage).
 *
 * WHO sees birthdays is NOT decided here (Petra, staging check 2026-11-02).
 * `GET /api/celebrations/upcoming` sends `birthday` items to an admin or HR
 * caller and name days to everyone, so this card renders whatever comes back
 * and is right for both audiences by construction. Deliberately no `isHR`
 * check in this file: a second copy of the rule could only ever drift from
 * the server's, and the server's is the one that keeps the data in.
 *
 * Czech state holidays stay a frontend computation: `date-holidays` is in
 * dependencies and they are deterministic from a date. The backend already
 * uses the same package for time-off math (`backend/src/utils/holidays.ts`),
 * so frontend results match. Name days and birthdays cannot work that way —
 * they need the team roster — so they come from
 * `GET /api/celebrations/upcoming`, which returns a name and a day/month and
 * never a birth date, a year or an age.
 *
 * With the flag off this file renders byte-for-byte the card it was before
 * MKS-392 ("Upcoming holidays", five holiday rows, no request).
 */
import { useMemo } from 'react';
import Holidays from 'date-holidays';
import { Card } from '../ui/Card';
import { useFlag } from '../../services/flagsContext';
import { useUpcomingCelebrations } from '../../hooks/useUpcomingCelebrations';
import type { Celebration } from '../../services/api/celebrations';
import { formatDayMonthShort, formatWeekdayShort } from '../../utils/dateFormat';

interface HolidayRow {
  date: Date;
  name: string;
}

type UpcomingRow = {
  date: Date;
  label: string;
  kind: 'holiday' | 'birthday' | 'nameday';
};

const LOOKAHEAD_DAYS = 30;

/** Rows shown with the flag off (unchanged) and with it on (celebrations crowd the list). */
const MAX_ROWS_HOLIDAYS_ONLY = 5;
const MAX_ROWS_WITH_CELEBRATIONS = 8;

function upcomingHolidays(now: Date): HolidayRow[] {
  const hd = new Holidays('CZ');
  const horizon = new Date(now.getTime() + LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000);
  const items: HolidayRow[] = [];

  // `date-holidays` iterates by year; cover this year + next year in case
  // the lookahead window crosses Dec 31.
  for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
    const list = hd.getHolidays(year) as Array<{ start: Date; name: string; type: string }>;
    for (const h of list) {
      // Only "public" holidays — skip "observance" / "bank" / "school".
      if (h.type !== 'public') continue;
      const d = new Date(h.start);
      if (d >= now && d <= horizon) {
        items.push({ date: d, name: h.name });
      }
    }
  }
  return items.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function fmt(d: Date): string {
  // MKS-351: English weekday + day/month from the shared util, so this never
  // renders `po 5. 7.` next to a `dd.mm.yyyy` date elsewhere on the dashboard.
  return `${formatWeekdayShort(d)} ${formatDayMonthShort(d)}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The next occurrence of a day/month inside the window.
 *
 * The API sends a month and a day with no year on purpose, so the year is
 * resolved here: this year if the date has not passed, otherwise next year —
 * which is what makes a late-December window show January name days.
 */
function nextOccurrence(month: number, day: number, today: Date): Date {
  const thisYear = new Date(today.getFullYear(), month - 1, day);
  return thisYear >= today ? thisYear : new Date(today.getFullYear() + 1, month - 1, day);
}

function celebrationLabel(c: Celebration, isToday: boolean): string {
  const occasion = c.type === 'birthday' ? 'birthday' : 'name day';
  // "Today: <name> has a birthday" on the day itself, a plain
  // "Birthday: <name>" before it. English per the repo's copy gate
  // (tests/unit/no-czech-in-v7.test.ts), which is why this reads "Name day"
  // rather than the ticket's "Jmeniny".
  if (isToday) return `Today: ${c.name} has a ${occasion}`;
  return c.type === 'birthday' ? `Birthday: ${c.name}` : `Name day: ${c.name}`;
}

export function HolidaysCard() {
  const celebrationsOn = useFlag('mks_392_upcoming_celebrations');

  // F-05a (staging walk 2026-06-30) — anchor the 30-day window to TODAY, not
  // the 1st of the month. `new Date(year, month)` defaults the day to 1, so by
  // late month the lookahead effectively ended days ago (e.g. on Jun 30 the
  // window closed Jul 1, hiding the Jul 5/6 CZ holidays) and the card showed
  // "No public holidays" all month. Memo on y/m/d so it recomputes per day.
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const holidays = useMemo(() => upcomingHolidays(new Date(y, m, d)), [y, m, d]);

  const { data: celebrations } = useUpcomingCelebrations(LOOKAHEAD_DAYS, celebrationsOn);

  const rows = useMemo<UpcomingRow[]>(() => {
    const today = new Date(y, m, d);
    const merged: UpcomingRow[] = holidays.map((h) => ({
      date: h.date,
      label: h.name,
      kind: 'holiday' as const,
    }));

    if (celebrationsOn) {
      for (const c of celebrations ?? []) {
        const date = nextOccurrence(c.month, c.day, today);
        merged.push({
          date,
          label: celebrationLabel(c, sameDay(date, today)),
          kind: c.type,
        });
      }
    }

    // Same day: holidays first, then the order the API already sorted into
    // (birthdays before name days, then alphabetical).
    return merged.sort((a, b) => {
      const byDate = a.date.getTime() - b.date.getTime();
      if (byDate !== 0) return byDate;
      if (a.kind === b.kind) return 0;
      return a.kind === 'holiday' ? -1 : 1;
    });
  }, [holidays, celebrations, celebrationsOn, y, m, d]);

  const today = new Date(y, m, d);
  const maxRows = celebrationsOn ? MAX_ROWS_WITH_CELEBRATIONS : MAX_ROWS_HOLIDAYS_ONLY;

  return (
    <Card title={celebrationsOn ? 'Upcoming' : 'Upcoming holidays'}>
      {rows.length === 0 ? (
        <p className="text-[12px] text-ms-text-muted">
          {celebrationsOn
            ? `Nothing coming up in the next ${LOOKAHEAD_DAYS} days.`
            : `No public holidays in the next ${LOOKAHEAD_DAYS} days.`}
        </p>
      ) : (
        <ul className="divide-y divide-ms-border">
          {rows.slice(0, maxRows).map((r, i) => {
            const isToday = celebrationsOn && sameDay(r.date, today);
            return (
              <li
                key={`${r.kind}-${r.date.toISOString()}-${i}`}
                className="py-2 flex items-center justify-between text-[12px]"
              >
                <span
                  className={`truncate ${
                    isToday ? 'text-ms-orange-dark font-semibold' : 'text-ms-text-strong'
                  }`}
                >
                  {r.label}
                </span>
                <span className="text-ms-text-muted tabular-nums shrink-0 ml-2">{fmt(r.date)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
